import { cn } from '@src/lib/utils';

import { useThemeStore } from '@shared/stores/theme';

import { Check, Info, LoaderCircle, TriangleAlert, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Toaster as Sonner } from 'sonner';

type ToasterProps = React.ComponentProps<typeof Sonner>;

const iconClassName = 'shrink-0 self-center';

const iconWrap = (type: 'success' | 'error', icon: React.ReactNode) => (
    <span className={iconClassName}>
        <span
            className={cn(
                'inline-flex h-5 w-5 items-center justify-center rounded-full text-white',
                type === 'success'
                    ? 'bg-(--color-brand-green)'
                    : 'bg-(--color-danger)',
            )}
        >
            {icon}
        </span>
    </span>
);

const Toaster = ({ ...props }: ToasterProps) => {
    const theme = useThemeStore((x) => x.theme);
    const { t } = useTranslation();

    return (
        <Sonner
            containerAriaLabel={t('translation:notifications')}
            position='top-center'
            theme={theme === 'dark' ? 'dark' : 'light'}
            icons={{
                success: iconWrap('success', <Check className='h-3 w-3' />),
                info: (
                    <span className={iconClassName}>
                        <Info className='h-5 w-5' />
                    </span>
                ),
                warning: (
                    <span className={iconClassName}>
                        <TriangleAlert className='h-5 w-5' />
                    </span>
                ),
                error: iconWrap('error', <X className='h-3 w-3' />),
                loading: (
                    <span className={iconClassName}>
                        <LoaderCircle className='h-5 w-5 animate-spin' />
                    </span>
                ),
            }}
            toastOptions={{
                unstyled: true,
                classNames: {
                    toast: cn(
                        'min-w-[356px] rounded-xl border border-(--color-border) bg-(--color-body) p-4 shadow-[0_4px_12px_rgba(0,0,0,0.15)]',
                        'flex flex-wrap items-center gap-3 font-[var(--font-family-body)]',
                    ),
                    content: 'flex min-w-0 flex-1 flex-col justify-center',
                    title: 'font-[var(--font-family-body)] text-sm font-semibold leading-5 text-(--color-card-text)',
                    description:
                        'mt-1 font-[var(--font-family-body)] text-[13px] font-normal leading-5 text-(--color-card-subtitle)',
                    icon: iconClassName,
                    cancelButton: 'ml-auto self-end',
                    actionButton: 'ml-0 self-end',
                    closeButton:
                        'flex items-center justify-center rounded-md p-1 opacity-70 transition hover:opacity-100',
                    loader: 'flex items-center justify-center',
                    success: 'bg-(--color-body) text-(--color-card-text)',
                    error: 'bg-(--color-body) text-(--color-card-text)',
                    info: 'bg-(--color-body) text-(--color-card-text)',
                    warning: 'bg-(--color-body) text-(--color-card-text)',
                    loading: 'bg-(--color-body) text-(--color-card-text)',
                    default: 'bg-(--color-body) text-(--color-card-text)',
                },
            }}
            {...props}
        />
    );
};

export { Toaster };
